import WorkspacePrimaryButton from "@/components/PrimaryButton";
import { Classroom, LessonWithSlides } from "@/types/tutor";
import { useEffect, useRef, useState } from "react";
import ClassroomAssignment from "./ClassroomAssignment";
import { debugLog } from "@/utils/debugLog";
import CleverAssignmentStatusBadge from "./CleverAssignmentStatusBadge";
import { Calculator, FileText, MoreHorizontal, Users } from "lucide-react";
import api from "@/utils/axios-utils";
import LessonContentDrawer from "./LessonContentDrawer";
import { formatLessonUpdatedAt } from "./lessonRows";
import { useAuthStore } from "@/store/useAuthStore";
import {
  NotificationType,
  useNotificationStore,
} from "@/store/useNotificationStore";

interface LessonCardProps {
  lesson: LessonWithSlides;
  classrooms: Classroom[];
  isExpanded: boolean;
  onToggle: () => void;
}

const LessonCard = ({
  lesson,
  classrooms,
  isExpanded,
  onToggle,
}: LessonCardProps) => {
  const displayName = lesson.name.trim() || "Untitled lesson";
  const actingTeacherId = useAuthStore((state) => state.actingTeacherId);
  const isOrgAdmin = useAuthStore((state) => state.user?.role === "ORG_ADMIN");
  const canAssignToGroup = !isOrgAdmin || actingTeacherId !== null;
  const { showNotification } = useNotificationStore();
  const [isLessonAssignmentOpen, setIsLessonAssignmentOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [calculatorLoading, setCalculatorLoading] = useState(false);
  const [calcState, setCalcState] = useState<{
    allEnabled: boolean;
    allDisabled: boolean;
  } | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const handleOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [menuOpen]);

  const openMenu = async () => {
    setMenuOpen((o) => !o);
    if (calcState !== null) return;
    try {
      const res = await api.get(`/lessons/${lesson.id}/calculator`);
      setCalcState({
        allEnabled: res.data.all_enabled,
        allDisabled: res.data.all_disabled,
      });
    } catch {
      setCalcState({ allEnabled: false, allDisabled: false });
    }
  };

  const showEnable = calcState !== null && !calcState.allEnabled;
  const showDisable = calcState !== null && !calcState.allDisabled;

  const handleSetCalculatorAll = async (enabled: boolean) => {
    setMenuOpen(false);
    setCalculatorLoading(true);
    try {
      await api.patch(`/lessons/${lesson.id}/calculator`, { enabled });
      setCalcState({ allEnabled: enabled, allDisabled: !enabled });
      showNotification(
        `Calculator ${enabled ? "enabled" : "disabled"}.`,
        NotificationType.SUCCESS,
      );
    } catch {
      showNotification(
        "Failed to update calculator setting.",
        NotificationType.WARNING,
      );
    } finally {
      setCalculatorLoading(false);
    }
  };

  const toggleLessonAssignment = () => {
    setIsLessonAssignmentOpen(!isLessonAssignmentOpen);
  };

  useEffect(() => {
    debugLog(lesson);
  }, [lesson]);

  return (
    <div className="relative">
      <article className="et-catalog-row">
        <FileText size={20} aria-hidden="true" />
        <div>
          <h3>
            <button
              type="button"
              className="et-lesson-title"
              onClick={onToggle}
              aria-haspopup="dialog"
              aria-expanded={isExpanded}
            >
              {displayName}
            </button>
          </h3>
          {classrooms.length > 0 && (
            <CleverAssignmentStatusBadge
              classroomId={classrooms[0]?.id ?? null}
              lessonId={lesson.id}
            />
          )}
        </div>
        <time dateTime={lesson.updated_at}>
          {formatLessonUpdatedAt(lesson.updated_at)}
        </time>
        <div className="et-row-actions">
          {canAssignToGroup && (
            <WorkspacePrimaryButton
              type="button"
              onClick={toggleLessonAssignment}
              className="et-primary-button"
            >
              <Users size={15} />
              <span>Assign to a group</span>
            </WorkspacePrimaryButton>
          )}
          <div
            className="relative"
            ref={menuRef}
            onKeyDown={(e) => {
              if (e.key === "Escape") setMenuOpen(false);
            }}
          >
            <button
              type="button"
              onClick={openMenu}
              disabled={calculatorLoading}
              aria-label={`Options for ${displayName}`}
              aria-expanded={menuOpen}
              className="et-icon-button"
            >
              <MoreHorizontal size={19} />
            </button>
            {menuOpen && (
              <div className="et-popover">
                {calcState === null ? (
                  <div className="px-4 py-2.5 text-sm text-gray-400">
                    Loading…
                  </div>
                ) : (
                  <>
                    {showEnable && (
                      <button
                        type="button"
                        onClick={() => handleSetCalculatorAll(true)}
                        className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm font-mulish text-gray-700 hover:bg-gray-50 transition-colors"
                      >
                        <Calculator size={15} className="text-gray-500" />
                        Enable calculator
                      </button>
                    )}
                    {showDisable && (
                      <button
                        type="button"
                        onClick={() => handleSetCalculatorAll(false)}
                        className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm font-mulish text-gray-700 hover:bg-gray-50 transition-colors"
                      >
                        <Calculator size={15} className="text-gray-400" />
                        Disable calculator
                      </button>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </article>
      {isLessonAssignmentOpen && (
        <ClassroomAssignment
          lesson={lesson}
          classrooms={classrooms}
          onClose={() => setIsLessonAssignmentOpen(false)}
        />
      )}
      <LessonContentDrawer
        lesson={lesson}
        isOpen={isExpanded}
        onToggle={onToggle}
        onAssign={
          canAssignToGroup ? () => setIsLessonAssignmentOpen(true) : undefined
        }
      />
    </div>
  );
};

export default LessonCard;
